import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import CountryFlag from '../CountryFlag/CountryFlag';
import { IconArrowHead } from '../index';
import css from './CountrySelector.module.css';
import classNames from 'classnames';

const CountrySelector = ({
    options,
    onSelect,
    currentUserCountry,
    rootClassName,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOption, setSelectedOption] = useState(null);
    const dropdownRef = useRef(null);

    // Convert options to our format
    const selectOptions = options.map(option => ({
        value: option.code,
        label: option.code,
        country: option.country,
    }));

    useEffect(() => {
        setSelectedOption(selectOptions.find(opt => opt.value === currentUserCountry.code) || null);
    }, [currentUserCountry]);

    // Filter options based on search term
    const filteredOptions = selectOptions.filter(option => 
        option.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
        option.country?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSelect = (option) => {
        setSelectedOption(option);
        onSelect(option.value);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleTriggerClick = () => {
        setIsOpen(!isOpen);
        setSearchTerm('');
    };

    const classes = classNames(css.root, rootClassName);

    return (
        <div className={classes} ref={dropdownRef}>
            {/* Custom Select Trigger */}
            <div 
                className={css.trigger}
                onClick={handleTriggerClick}
            >
                <div className={css.triggerContent}>
                    {selectedOption && (
                        <>
                            <CountryFlag code={selectedOption.value} height="20" width="20" />
                            <span className={css.countryCode}>{selectedOption.value}</span>
                        </>
                    )}
                    <IconArrowHead
                        direction={isOpen ? "up" : "down"}
                        size="small"
                        rootClassName={classNames(css.arrowIcon, { [css.arrowOpen]: isOpen })}
                    />
                </div>
            </div>

            {/* Custom Dropdown Menu */}
            {isOpen && (
                <div className={css.dropdownMenu}>
                    {/* Search Input */}
                    <div className={css.searchContainer}>
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            className={css.searchInput}
                            autoFocus
                        />
                    </div>
                    
                    {/* Options List */}
                    <div className={css.optionsList}>
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map(option => (
                                <div
                                    key={option.value}
                                    className={classNames(css.option, {
                                        [css.optionSelected]: selectedOption?.value === option.value
                                    })}
                                    onClick={() => handleSelect(option)}
                                >
                                    <CountryFlag code={option.value} height="20" width="20" />
                                    <span className={css.optionCode}>{option.value}</span>
                                    <span className={css.optionCountry}>{option.country}</span>
                                </div>
                            ))
                        ) : (
                            <div className={css.noOptions}>
                                No countries found
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

CountrySelector.defaultProps = {
    rootClassName: null,
};

CountrySelector.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape({
            code: PropTypes.string.isRequired,
            country: PropTypes.string,
        })
    ).isRequired,
    onSelect: PropTypes.func.isRequired,
    currentUserCountry: PropTypes.shape({
        code: PropTypes.string.isRequired,
    }).isRequired,
    rootClassName: PropTypes.string,
};

export default CountrySelector;